import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Strophe } from 'react-strophe'
import api from '../api/chat'
import orchestrationApi from '../api/orchestration'
import { needToAddBookmark } from '../global/bookmark-helper'
import { isPrivateChatRoom } from '../global/build-jid'
import chatLog from '../global/chat-log'
import { useChatState } from '../state/ChatState'
import useRequestState from './useRequestState'

/*
  This hook will join a private group (by invitation), then bookmark the group
  for later use.
*/
export default function useJoinGroupByInvitation() {
  const [t] = useTranslation()
  const { connection, user, refetchBookmarks, bookmarks } = useChatState()
  const fromJid = user.authzid
  const [joinFn, requestState] = useRequestState(api.groups.joinGroupChat)

  const joinGroupByInvitation = useCallback(
    async (chatRoom) => {
      try {
        await joinFn({ chatRoom, fromJid, connection })

        let bookmarkTitle

        if (isPrivateChatRoom(chatRoom)) {
          // NOTE: Since private chat groups are not saved to the server (only if you've bookmarked it), then we pluck the name from the chat server room config.
          const chatRoomDetails = await api.search.searchForChatRoomFromChatServer({ connection, chatRoom })

          bookmarkTitle = chatRoomDetails?.length ? chatRoomDetails[0].friendlyName : t('chat.unknown-name', 'Unknown')
        } else {
          const chatRoomDetails = await orchestrationApi.chatgroups.getChatRoom({
            jid: Strophe.getNodeFromJid(chatRoom),
          })
          bookmarkTitle = chatRoomDetails?.data?.title || t('chat.unknown-name', 'Unknown')
        }

        let needBookmark = needToAddBookmark({ bookmarks, jid: chatRoom })

        // RTC-955: Since others can add bookmarks for me on private group chats, check to see if it already exists.
        if (needBookmark && isPrivateChatRoom(chatRoom)) {
          const lookupResults = await orchestrationApi.bookmarks.getBookmarkByJid({ jid: chatRoom })
          needBookmark = lookupResults?.data?.bookmarks?.length === 0
        }

        if (needBookmark) {
          await orchestrationApi.bookmarks.addBookmark({ jid: chatRoom, name: bookmarkTitle })
          refetchBookmarks()
        }

        chatLog(`[Groups] Joined private group and bookmarked: ${chatRoom}`)
      } catch (error) {
        console.error(`There was a problem joining ${chatRoom}. Error: `, error)
      }
    },
    [bookmarks, connection, fromJid, joinFn, refetchBookmarks, t]
  )
  return [joinGroupByInvitation, requestState]
}
